import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiAlert from "components/SuiAlert";
import { CircularProgress } from "@mui/material";
import ProcesosApi from "api/procesosApi";
import { styles } from "../styles";

const MIN_PALABRAS_CAUSAS = 20;
const contarPalabras = (texto) => {
  if (!texto) return 0;
  return texto
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
};

export const validarCausa = async (procesoId) => {
  try {
    const response = await ProcesosApi.fetchProcesoById(procesoId);
    const proceso = response.data;
    const palabrasCausas = contarPalabras(proceso.causas);
    const analisis = {
      results: {
        causas: {
          texto: proceso.causas || "",
          palabras: palabrasCausas,
          palabrasMinimas: MIN_PALABRAS_CAUSAS,
          cumpleMinimo: palabrasCausas >= MIN_PALABRAS_CAUSAS,
        },
      },
    };
    const completed = analisis.results.causas.cumpleMinimo;

    return {
      results: analisis,
      completed: completed,
      error: completed ? false : true,
      errorMessage: completed
        ? ""
        : `La Solicitud requiere una causa más detallada: ` +
          `${
            !analisis.results.causas.cumpleMinimo
              ? `${palabrasCausas}/${MIN_PALABRAS_CAUSAS} palabras requeridas en el campo Causas.`
              : ""
          }`,
    };
  } catch (error) {
    console.error("Error al validar la Causa de la Solicitud:", error);
    throw error;
  }
};

const estadoInicial = {
  completed: false,
  error: false,
  errorMessage: "",
  results: {
    causas: {
      texto: "",
      palabras: 0,
      palabrasMinimas: MIN_PALABRAS_CAUSAS,
      cumpleMinimo: false,
    },
  },
};

const VerificacionCausa = ({
  procesoId,
  onStepValidation = null,
  showResults = false,
  initialData = null,
}) => {
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [analisis, setAnalisis] = useState(initialData || estadoInicial);

  useEffect(() => {
    const verificar = async () => {
      if (initialData) {
        setAnalisis(initialData);
        if (onStepValidation) {
          onStepValidation(initialData.completed, initialData.errorMessage);
        }
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const resultado = await validarCausa(procesoId);
        setAnalisis(resultado);
        if (onStepValidation) {
          onStepValidation(resultado.completed, resultado.errorMessage);
        }
      } catch (error) {
        console.error("Error al verificar la Causa de la Solicitud:", error);
        setError(obtenerMensajeError(error, "Error al verificar la Causa de la Solicitud"));
        if (onStepValidation) {
          onStepValidation(false, "Error al verificar la Causa de la Solicitud");
        }
      } finally {
        setLoading(false);
      }
    };
    verificar();
  }, [procesoId, onStepValidation, initialData]);

  if (loading) {
    return (
      <SuiBox sx={styles.loadingContainer}>
        <CircularProgress />
      </SuiBox>
    );
  }

  if (error) {
    return (
      <SuiBox sx={styles.errorContainer}>
        <SuiAlert color="error">{error}</SuiAlert>
      </SuiBox>
    );
  }

  if (!showResults) {
    return null;
  }

  const { causas } = analisis.analisis;

  return (
    <Box sx={styles.root}>
      <SuiBox mb={3}>
        <SuiBox mb={2}>
          <SuiTypography variant="h6" fontWeight="medium">
            Causas
          </SuiTypography>
          <SuiBox mt={1} p={2} sx={causas.cumpleMinimo ? styles.textoValido : styles.textoInvalido}>
            <SuiTypography variant="button" fontWeight="regular">
              {causas.texto || "Sin causas"}
            </SuiTypography>
            <SuiTypography variant="caption" color={causas.cumpleMinimo ? "success" : "error"}>
              {causas.palabras} palabras
              {!causas.cumpleMinimo && ` (mínimo ${MIN_PALABRAS_CAUSAS})`}
            </SuiTypography>
          </SuiBox>
        </SuiBox>
      </SuiBox>
    </Box>
  );
};

VerificacionCausa.propTypes = {
  procesoId: PropTypes.number.isRequired,
  onStepValidation: PropTypes.func,
  showResults: PropTypes.bool,
  initialData: PropTypes.object,
};

export default VerificacionCausa;
